import React, { useState, useEffect, useRef, useCallback } from 'react';
import sendMessageIcon from '../images/send-message-icon.svg';
import sendMessageIconDark from '../images/send-message-icon-dark.svg';
import scrollIcon from '../images/scroll-icon.svg';
import scrollIconDark from '../images/scroll-icon-dark.svg';
import backIcon from '../images/back-icon.svg';
import threeDotsIcon from '../images/three-dots-icon.svg';
import addAttachmentIcon from '../images/add-attachment-icon.svg';
import UploadMenu from './UploadMenu/uploadMenu';
import Navigation from './Navigation/navigation';
import closeIcon from '../images/close-icon.svg';
import DiaProfilePicture from '../images/dia-profile-picture.jpeg';
import Profile from '../Profile/profile';
import SubscriptionModal from '../components/SubscriptionModal/SubscriptionModal';
import './chat.css';
import { apiCall } from '../utils/api';
import choosePhotoIcon from '../images/choose-photo-icon.svg';
import trackEvent from '../utils/analytics.js';
import GoogleLogo from "../images/google-logo.svg";
import memoryFeatureLogo from '../images/memory.svg';
import personalisationFeatureLogo from '../images/personalization.svg';
import smartRepliesFeatureLogo from '../images/smart_replies.svg';
import { messaging, getToken, onMessage } from '../firebase.js';
import Login from './Login/login.js';
import shareIcon from '../images/share-icon.svg';
import readTickGreyIcon from '../images/read-tick-grey-icon.svg';
import readTickWhiteIcon from '../images/read-tick-white-icon.svg';
import micIcon from '../images/mic-icon.svg';
import micIconDark from '../images/mic-icon-dark.svg';
import playIcon from '../images/play-icon.svg';
import pauseIcon from '../images/pause-icon.svg';
import deleteIcon from '../images/delete-icon.svg';
import notificationSoundFile from '../notification-sound.mp3';
import instagramIcon from "../images/instagram-icon.png";
import whatsappIcon from "../images/whatsapp-icon.svg";

const renderTextMessages = (text) => {
    if (!text) return text;

    // Enhanced URL regex pattern
    const urlRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?(?:\/[^\s]*)?)/g;

    // Bold text regex pattern - matches text between asterisks
    const boldRegex = /\*(.*?)\*/g;

    // Identify parts that are URLs, bold text, or plain text
    const parts = [];
    let lastIndex = 0;

    // Create a combined regex to match both URLs and bold text
    const combinedPattern = new RegExp(`${urlRegex.source}|${boldRegex.source}`, 'g');
    let match;

    while ((match = combinedPattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex, match.index)
            });
        }

        // Check if it's a URL or bold text
        if (match[0].startsWith('*') && match[0].endsWith('*')) {
            // Bold text - extract content between asterisks
            const boldContent = match[0].substring(1, match[0].length - 1);
            parts.push({
                type: 'bold',
                content: boldContent
            });
        } else {
            // URL
            parts.push({
                type: 'url',
                content: match[0]
            });
        }

        lastIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
        parts.push({
            type: 'text',
            content: text.substring(lastIndex)
        });
    }

    // Convert to React elements
    return parts.map((part, index) => {
        if (part.type === 'text') {
            return <span key={`text-${index}`}>{part.content}</span>;
        } else if (part.type === 'bold') {
            return <strong key={`bold-${index}`}>{part.content}</strong>;
        } else {
            // URL part
            let href = part.content;
            if (!href.match(/^https?:\/\//)) {
                href = `https://${href}`;
            }

            return (
                <a
                    key={`link-${index}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="message-link"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {part.content}
                </a>
            );
        }
    });
};

const Chat = ({
    isUnidentifiedUser,
    darkMode,
    toggleDarkMode,
    selectedCharacter,
    onBackToCharacterSelection,
    hasSubscription: hasSubscriptionProp,
    variant = 'default',
    displayName,
    displayAvatarUrl,
}) => {
    const FREE_ALLOWED_MESSAGES = 10; // Free messages allowed for unidentified users
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [inputPlaceholder, setInputPlaceholder] = useState(() => {
        if (variant === 'deva') return "Say what's on your mind";
        return selectedCharacter ? `Talk to ${selectedCharacter.name}` : 'Talk to Dia';
    });
    const [pendingMessages, setPendingMessages] = useState([]);
    const [typingTimeout, setTypingTimeout] = useState(null);
    const [showNavMenu, setShowNavMenu] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [profileData, setProfileData] = useState({
        name: 'Dia',
        bio: 'Life is good when you have me',
        image_url: null
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isTyping, setIsTyping] = useState(false);
    const typingDelay = 1000; // Delay between messages in milliseconds
    const [lastRecordId, setLastRecordId] = useState(null);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: '', message: '', action: null });
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportDescription, setReportDescription] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageInModal, setImageInModal] = useState(null);
    const [profileImageInModal, setProfileImageInModal] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [userMessageCount, setUserMessageCount] = useState(0);
    const [showCopied, setShowCopied] = useState(false);
    const [sendingMessageIds, setSendingMessageIds] = useState(new Set());
    const [userId, setUserId] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingInterval, setRecordingInterval] = useState(null);
    const [audioElements, setAudioElements] = useState(new Map()); // Track audio elements
    const [playingAudios, setPlayingAudios] = useState(new Set()); // Track which audios are playing
    const [isTabActive, setIsTabActive] = useState(true); // Track if tab is active
    const [notificationSound, setNotificationSound] = useState(null); // Notification sound element
    const [unreadCount, setUnreadCount] = useState(0); // Track unread message count
    const [showUpdateModal, setShowUpdateModal] = useState(false); // Track update modal visibility
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false); // Track subscription modal visibility
    const [hasSubscription, setHasSubscription] = useState(hasSubscriptionProp || false); // Track subscription status from prop or default to false
    const isCancelledRef = useRef(false);
    const recordingTimeRef = useRef(0);
    const isTabActiveRef = useRef(true); // Use ref to track current tab state
    const VAPID_KEY = process.env.FIREBASE_VAPID_KEY;

    // Add ref for messages area
    const messagesEndRef = React.useRef(null);

    // Update useEffect to scroll only for new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, selectedImage]);

    // Update input placeholder when selectedCharacter changes
    useEffect(() => {
        if (variant === 'deva') {
            setInputPlaceholder("Say what's on your mind");
            return;
        }
        if (selectedCharacter?.name) {
            setInputPlaceholder(`Talk to ${selectedCharacter.name}`);
        } else {
            setInputPlaceholder('Talk to Dia');
        }
    }, [selectedCharacter?.name, variant]);

    // Prefill input when chatting with Arun Pandit
    useEffect(() => {
        const name = selectedCharacter?.name?.toLowerCase() || '';
        if (name.includes('arun') && name.includes('pandit')) {
            setInputMessage((prev) => prev || 'Naukri nahi lag rahi pandit ji...');
        }
    }, [selectedCharacter?._id, selectedCharacter?.name]);

    // Resize textarea when inputMessage changes (for prepopulated text)
    useEffect(() => {
        const textarea = document.querySelector('.message-input');
        if (textarea && inputMessage) {
            textarea.style.height = 'auto';
            const newHeight = Math.min(textarea.scrollHeight, 120);
            textarea.style.height = Math.max(newHeight, 16) + 'px';
        }
    }, [inputMessage]);

    // Add this cleanup effect to handle audio elements and blob URLs:
    useEffect(() => {
        return () => {
            // Cleanup audio elements on component unmount
            audioElements.forEach(audio => {
                audio.pause();
                audio.src = '';
                // Clean up Safari-specific blob URLs
                if (audio._safariBlobUrl) {
                    URL.revokeObjectURL(audio._safariBlobUrl);
                }
            });
            setAudioElements(new Map());
            setPlayingAudios(new Set());

            // Cleanup recording interval
            if (recordingInterval) {
                clearInterval(recordingInterval);
            }

            // Cleanup blob URLs ONLY on unmount (page refresh or navigation away)
            messages.forEach(message => {
                if (message.image && message.image.startsWith('blob:')) {
                    URL.revokeObjectURL(message.image);
                }
                if (message.voice && message.voice.startsWith('blob:')) {
                    URL.revokeObjectURL(message.voice);
                }
            });
        };
    }, []);

    // Initialize notification sound
    useEffect(() => {
        // Create notification sound using the MP3 file
        const createNotificationSound = () => {
            try {
                const audio = new Audio(notificationSoundFile);
                audio.volume = 1; // Set volume to 30%
                return audio;
            } catch (error) {
                console.error('Error creating notification sound:', error);
                return null;
            }
        };

        setNotificationSound(createNotificationSound);
    }, []);

    // Track tab visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            const newTabActive = !document.hidden;
            setIsTabActive(newTabActive);
            isTabActiveRef.current = newTabActive; // Update ref immediately
            
            // Reset unread count when tab becomes active
            if (newTabActive) {
                setUnreadCount(0);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Update document title with unread count
    useEffect(() => {
        const updateTitle = () => {
            if (unreadCount > 0) {
                document.title = `Dia (${unreadCount})`;
            } else {
                document.title = 'Dia';
            }
        };
        
        updateTitle();
    }, [unreadCount]);


    // const updateFcmToken = async (fcmToken) => {
    //     await apiCall.post("/notification/update/fcm/token", { "fcm_token": fcmToken });
    // };

    // Optional: Make scroll behavior more smooth by adding a small delay
    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            // Hide scroll button when scrolling to bottom
            setTimeout(() => {
                setShowScrollButton(false);
            }, 500); // Wait for scroll animation to complete
        }, 100);
    };

    // Add keyboard handler
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    // Helper functions for JWT parsing (moved before useEffect that uses loadUserProfile)
    const getAccessToken = () => {
        return document.cookie
            .split('; ')
            .find(cookie => cookie.startsWith('access_token='))
            ?.split('=')[1] || null;
    };

    const parseJwt = (token) => {
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        try {
            const payload = parts[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            const decoded = atob(payload);
            const uriDecoded = decodeURIComponent(
                decoded.split('')
                    .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join('')
            );
            return JSON.parse(uriDecoded);
        } catch (error) {
            console.error('Failed to parse JWT payload:', error);
            return null;
        }
    };

    const loadUserProfile = useCallback(async () => {
        try {
            const userDetailsResponse = await apiCall.get('/users/get/user/details');
            const userDetailsPayload = userDetailsResponse?.data || userDetailsResponse;
            const normalizedUser = userDetailsPayload?.data || userDetailsPayload;

            setProfileData(prev => ({
                ...prev,
                ...normalizedUser,
                name: normalizedUser?.name || prev.name,
                bio: normalizedUser?.bio || prev.bio,
                image_url: normalizedUser?.image_url || prev.image_url,
            }));

            const token = getAccessToken();
            const jwtPayload = parseJwt(token);
            const odUserId =
                jwtPayload?.user_id ||
                jwtPayload?.data?.user_id ||
                normalizedUser?._id ||
                normalizedUser?.user_id;

            if (!odUserId) {
                console.warn('User ID not available for onboarding fetch');
                return;
            }

            const onboardingResponse = await apiCall.get(`/users/onboarding/${odUserId}`);
            const onboardingPayload = onboardingResponse?.data || onboardingResponse;
            if (!onboardingPayload) {
                return;
            }

            const onboardingData = onboardingPayload?.data || onboardingPayload;
            setProfileData(prev => ({
                ...prev,
                ...onboardingData,
                onboarding: onboardingData,
                onboarding_choices: onboardingData,
            }));
        } catch (error) {
            console.error('Failed to load onboarding profile data:', error);
        }
    }, []);

    // Update useEffect to handle loading and scroll together
    useEffect(() => {
        const init = async () => {
            await fetchChatHistory();
            scrollToBottom();
            // Only remove loading after everything is ready and scrolled
            setTimeout(() => {
                setIsLoading(false);
            }, 500); // Small delay to ensure smooth transition
        };

        const urlObj = new URL(window.location.href);

        // Get all search parameters
        const params = new URLSearchParams(urlObj.search);

        // Convert URLSearchParams to a regular object
        const paramsObj = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });
        trackEvent('chat_page_opened', paramsObj);

        init();
        loadUserProfile();

    }, [loadUserProfile]);

    useEffect(() => {
        if (isUnidentifiedUser && !showLoginModal) {
            const interval = setInterval(() => {
                setShowInfoModal(true);
            }, 300000);

            return () => clearInterval(interval);
        }
    }, [isUnidentifiedUser, showLoginModal]);

    // Handle clicks outside navigation menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the click is on the navigation toggle button
            const isToggleButton = event.target.closest('.three-dots-icon, .close-icon');
            if (isToggleButton) {
                return; // Don't handle clicks on the toggle button
            }
            
            // Check if the click is within the navigation menu container
            const isNavMenu = event.target.closest('.nav-menu-container');
            if (isNavMenu) {
                return; // Don't handle clicks within the navigation menu
            }
            
            if (showNavMenu) {
                setShowNavMenu(false);
            }
        };

        if (showNavMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNavMenu]);

    // Add scroll handler
    const handleScroll = () => {
        const messagesArea = document.querySelector('.messages-area');
        if (messagesArea) {
            const { scrollTop, scrollHeight, clientHeight } = messagesArea;
            setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
        }
    };

    const copyToClipboard = (text) => {
        navigator?.clipboard?.writeText(text).then(() => {
            setShowCopied(true);
            setTimeout(() => setShowCopied(false), 2000); // Hide after 2 seconds
        });
    };

    const handleShare = async () => {
        const shareText = "Hey! \nYou've got to meet my new best friend Dia. \nShe's funny, non-judgmental, and understands me like no one else and available 24x7.";
        let shareUrl = window.location.origin + window.location.pathname + "?utm_source=sharing";
        
        // Check if source is android and version is 3 or higher
        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        const source = params.get('source');
        const appVersion = params.get('app_version');
        
        // Helper function to check if version is 3.0.0 or higher
        const isVersion3OrHigher = (version) => {
            if (!version) return false;
            const versionParts = version.split('.').map(part => parseInt(part, 10));
            return versionParts[0] >= 3;
        };
        
        // Use Android bridge if source is android and version is 3 or higher
        if (source === 'android' && isVersion3OrHigher(appVersion)) {
            try {
                shareUrl="https://play.google.com/store/apps/details?id=com.chaimate.dia&hl=en";
                window.AndroidBridge.shareText(shareText + "\n" + shareUrl);
                trackEvent('share_clicked');
                return;
            } catch (error) {
                console.log('Android bridge ShareText failed, falling back to regular share:', error);
            }
        }
        
        // Regular sharing logic
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Meet Dia - My AI Best Friend',
                    text: shareText,
                    url: shareUrl
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback for browsers that don't support Web Share API
            copyToClipboard(shareText + "\n" + shareUrl);
        }
        trackEvent('share_clicked');
    };

    const handleInstagramClick = () => {
        const username = selectedCharacter?.social_media_handle?.instagram;
        if (!username) return;
        trackEvent('instagram_profile_clicked');
        try {
            window.AndroidBridge.openDeeplink(`instagram://user?username=${username}`);
        } catch (error) {
        }
    };

    const handleWhatsAppClick = () => {
        trackEvent('whatsapp_support_clicked');
        const supportNumber = '918209561672'; // Replace with actual support number
        const characterName = selectedCharacter?.name || '';
        const message = encodeURIComponent(`Hello, I need support - ${characterName}`);
        const whatsappUrl = `https://wa.me/${supportNumber}?text=${message}`;
        
        try {
            if (window.AndroidBridge && window.AndroidBridge.openDeeplink) {
                window.AndroidBridge.openDeeplink(`whatsapp://send?phone=${supportNumber}&text=${message}`);
            } else {
                window.open(whatsappUrl, '_blank');
            }
        } catch (error) {
            window.open(whatsappUrl, '_blank');
        }
    };

    const handleUpdateClick = () => {
        trackEvent('update_app_clicked');
        try {
            window.AndroidBridge.openDeeplink('market://details?id=com.chaimate.dia');
        } catch (error) {
            // Fallback to web URL if deep link fails
            window.open('https://play.google.com/store/apps/details?id=com.chaimate.dia', '_blank');
        }
        setShowUpdateModal(false);
    };

    const handleCloseUpdateModal = () => {
        // Don't allow closing the update modal - user must update
        // Modal stays open until user clicks Update button
    };

    const handleSubscriptionPurchase = async (amount, duration) => {
        trackEvent('subscription_clicked', { amount, duration });
        try {
            window.AndroidBridge.openRazorPay(amount);
        } catch (error) {
            console.error('AndroidBridge.openRazorPay not available:', error);
            // Could show an error message here
        }
        setShowSubscriptionModal(false);
    };

    const handleCloseSubscriptionModal = () => {
        trackEvent('subscription_modal_closed');
        setShowSubscriptionModal(false);
    };

    // Add scroll event listener
    useEffect(() => {
    //     try {
    //         Notification.requestPermission().then(permission => {
    //             if (permission === "granted") {
    //                 getToken(messaging, { vapidKey: VAPID_KEY })
    //                     .then(currentToken => {
    //                         if (currentToken) {
    //                             updateFcmToken(currentToken);
    //                         } else {
    //                             console.warn("No registration token available.");
    //                         }
    //                     })
    //                     .catch(err => {
    //                         console.error("An error occurred while retrieving token. ", err);
    //                     });
    //             }
    //         });

    //         onMessage(messaging, (payload) => {
    //             console.log("Message received. ", payload);
    //             const { title, body, icon } = payload.notification || {};
    //             // Check if browser supports notifications
    //             if (Notification.permission === "granted") {
    //                 new Notification(title || "New Message", {
    //                     body: body || "",
    //                     icon: icon || "/default-icon.png", // Optional icon
    //                 });
    //             } else {
    //                 console.warn("Notifications are not permitted.");
    //             }
    //         });
    //     }
    //     catch (err) {
    //     }


        const messagesArea = document.querySelector('.messages-area');
        if (messagesArea) {
            messagesArea.addEventListener('scroll', handleScroll);
            // Initialize scroll button state based on current scroll position
            handleScroll();
            return () => messagesArea.removeEventListener('scroll', handleScroll);
        }
    }, []);



    const fetchChatHistory = async (lastId = null, limit = 500) => {
        try {
            setIsLoadingMore(true);
            const queryParams = new URLSearchParams({
                limit: limit.toString(),
            });

            if (lastRecordId) {
                queryParams.append('last_record_id', lastRecordId);
            }

            let data = null;
            if (isUnidentifiedUser) {
                function generateUUID() {
                    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                        const r = Math.random() * 16 | 0;
                        const v = c === "x" ? r : (r & 0x3 | 0x8);
                        return v.toString(16);
                    });
                }
                let userId = generateUUID();
                localStorage.setItem('user_id', userId);
                queryParams.append('user_id', userId);
                queryParams.append('character_id', selectedCharacter?._id);
                data = await apiCall.get(`${process.env.REACT_APP_API_BASE_URL}/chat/v2?${queryParams.toString()}`);
                setShowInfoModal(true);
            }
            else {
                queryParams.append('character_id', selectedCharacter?._id);
                data = await apiCall.get(`/chat?${queryParams.toString()}`);
            }

            if (data.status && data.data) {

                const isConversationNew = !data.data.some(msg => msg.type === 2);
                // Remove setInputPlaceholder here
                // if (isConversationNew) {
                //     setInputPlaceholder(`Hey ${profileData?.name || 'Dia'}, tumse milke achha laga..`);
                // }
                // else {
                //     setInputPlaceholder(`Talk to ${profileData?.name || 'Dia'}`);
                // }

                let formattedMessages = data.data.map((msg, index) => ({
                    id: msg._id,
                    sender: msg.type === 2 ? 'me' : 'other',
                    text: msg.text,
                    image: msg.img_url, // Use server URL for rendering from history
                    voice: msg.voice_url, // Use server URL for rendering from history
                    serverImageUrl: msg.img_url, // Store server URL for potential future use
                    serverVoiceUrl: msg.voice_url, // Store server URL for potential future use
                    timestamp: msg.c * 1000 || new Date(),
                    time: new Date(msg.c * 1000 || new Date()).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                }));

                setHasMoreMessages(formattedMessages.length > 0);

                if (formattedMessages.length > 0) {
                    setLastRecordId(formattedMessages[0].id);
                }
                const shouldAddIntro = !lastId && formattedMessages.length === 0 && selectedCharacter?.intro_message;
                if (shouldAddIntro) {
                    const introMessage = {
                        id: `${selectedCharacter?._id || 'intro'}-initial`,
                        sender: 'other',
                        text: selectedCharacter.intro_message,
                        image: null,
                        voice: null,
                        serverImageUrl: null,
                        serverVoiceUrl: null,
                        timestamp: Date.now(),
                        time: new Date().toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                    };
                    formattedMessages = [introMessage];
                    setHasMoreMessages(false);
                    setLastRecordId(introMessage.id);
                }

                // Check if there are no user messages and set default "Hello" in input
                const hasUserMessages = formattedMessages.some(msg => msg.sender === 'me');
                // Remove setInputMessage from here
                // if (!hasUserMessages) {
                //     setInputMessage(`Hey ${profileData?.name || 'Dia'}, tell me, tum kya kya kr skti ho?`);
                // }

                // If this is a pagination request (lastId exists), prepend messages
                // Otherwise, set as new messages
                setMessages(prev => lastId ? [...formattedMessages, ...prev] : formattedMessages);
            }
        } catch (error) {
            console.error('Failed to fetch chat history:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    function formatDate(inputDate) {
        // Get today's date for comparison
        const today = new Date();

        // Try to parse the input date, and fall back to current date if invalid
        let date;
        try {
            // If inputDate is undefined/null/invalid, this will throw an error
            date = inputDate ? new Date(inputDate) : new Date();

            // Check if date is valid
            if (isNaN(date.getTime())) {
                // If invalid, fall back to current date
                console.log("Invalid date provided, falling back to current date");
                date = new Date();
            }
        } catch (error) {
            console.log("Error parsing date, falling back to current date:", error);
            date = new Date();
        }

        // Format the date
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
    }

    const formatMessageDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Yesterday";
        } else {
            return formatDate(date)
        }
    };

    const renderMessagesWithDateSeparators = () => {
        if (messages.length === 0) return null;

        const result = [];
        let currentDate = null;

        messages.forEach((message, index) => {
            let messageDate = new Date(message.timestamp);
            if (isNaN(messageDate)) {
                messageDate = new Date();
            }
            const messageDateStr = messageDate.toDateString();

            // Add date separator when date changes
            if (!currentDate || messageDateStr !== currentDate) {
                currentDate = messageDateStr;
                result.push(renderDateSeparator(message.timestamp));
            }

            // Add the message
            result.push(renderMessage(message));
        });
        return result;
    };

    const renderDateSeparator = (date) => {
        return (
            <div className="date-separator" key={`date-${date}`}>
                <div className="date-text">{formatMessageDate(date)}</div>
            </div>
        );
    };

    const renderMessage = (message) => {
        const isMe = message.sender === 'me';
        const isSending = sendingMessageIds.has(message.id);
        const isPending = pendingMessages.some(pm => pm.id === message.id);
        const shouldShowGrey = isPending && !isSending;
        const isPlaying = playingAudios.has(message.id);

        const handleImageClick = () => {
            // If user doesn't have subscription and image is from Dia (other messages), show subscription modal
            // if (!hasSubscription && !isMe) {
            //     setShowSubscriptionModal(true);
            // }
            // Use local URL if available, otherwise fall back to server URL
            const imageUrl = message.image || message.serverImageUrl;
            setImageInModal(imageUrl);
        };

        const handleImageError = async (message) => {
            // If the image fails to load and we have a server URL, try to fetch it
            if (message.serverImageUrl) {
                try {
                    const response = await fetch(message.serverImageUrl);
                    if (response.ok) {
                        const blob = await response.blob();
                        const localUrl = URL.createObjectURL(blob);
                        
                        // Clean up old blob URL if it exists
                        if (message.image && message.image.startsWith('blob:')) {
                            URL.revokeObjectURL(message.image);
                        }
                        
                        // Update the message with the local URL
                        setMessages(prev => prev.map(msg => 
                            msg.id === message.id 
                                ? { ...msg, image: localUrl }
                                : msg
                        ));
                    }
                } catch (error) {
                    console.error('Failed to load image:', error);
                }
            }
        };

        return (
            <div
                key={message.id}
                className={`message-container ${isMe ? 'me' : 'other'}`}
            >
                {message.voice ? (
                    <div className={`message-bubble voice-message ${isMe ? 'me' : 'other'}`}>
                        <div className="voice-message-content">
                            <button
                                className="voice-play-button"
                                onClick={() => { trackEvent('voice_message_play_clicked', { messageId: message.id }); toggleVoicePlayback(message.id, message.voice); }}
                            >
                                <img
                                    src={isPlaying ? pauseIcon : playIcon}
                                    alt={isPlaying ? "Pause" : "Play"}
                                    className="voice-control-icon"
                                />
                            </button>

                            {/* WhatsApp-style waveform visualization */}
                            <div className="voice-waveform">
                                {
                                    Array.from({ length: 20 }, (_, i) => {
                                        // Use a stable height based on index to prevent re-rendering
                                        const baseHeight = 12 + (i % 3) * 4; // Creates a pattern: 8, 12, 16, 8, 12, 16...` 
                                        const height = isPlaying ? `${baseHeight + Math.random() * 8}px` : `${baseHeight}px`;

                                        return (
                                            <div
                                                key={i}
                                                className={`waveform-bar ${isPlaying ? 'playing' : 'not-playing'}`}
                                                style={{
                                                    height: height,
                                                    animationDelay: `${i * 0.1}s`
                                                }}
                                            />
                                        )
                                    }
                                    )}
                            </div>
                        </div>
                        <div className="voice-message-footer">
                            <div className="message-time">{message.time}</div>
                            {isMe && (
                                <img
                                    src={shouldShowGrey ? readTickGreyIcon : readTickWhiteIcon}
                                    className="message-status-icon"
                                    alt="Message status"
                                    style={{ height: '10px', marginLeft: '4px' }}
                                />
                            )}
                        </div>
                    </div>
                ) : message.image ? (
                    // Image message bubble (existing code)
                    <div className={`message-bubble image-message ${isMe ? 'me' : 'other'}`}>
                        <div className="image-container">
                            <img
                                src={message.image}
                                alt="Shared image"
                                className={`message-image ${!hasSubscription && !isMe ? 'blur-image' : ''}`}
                                onClick={handleImageClick}
                                onError={() => handleImageError(message)}
                            />
                        </div>
                        {message.text && <div style={{ width: '100%', textAlign: 'left' }}>
                            <div className="message-text">{renderTextMessages(message.text)}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                                    <div className="message-time">{message.time}</div>
                                    <img
                                        src={readTickWhiteIcon}
                                        style={{ height: '10px', marginLeft: '4px' }}
                                    />
                                </div>
                            </div>
                        </div>}
                    </div>
                ) : (
                    // Text message bubble (existing code)
                    <div className={`message-bubble ${isMe ? 'me' : 'other'}`}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'end' }}>
                            <div>
                                <div>{renderTextMessages(message.text)}</div>
                                <div className="message-time">{message.time}</div>
                            </div>
                            <div>
                                {isMe && (
                                    <img
                                        src={shouldShowGrey ? readTickGreyIcon : readTickWhiteIcon}
                                        style={{ height: '10px', marginLeft: '4px' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const addMessageWithDelay = async (messages, index = 0) => {
        if (index < messages.length) {
            setIsTyping(true);

            // Wait for typing indicator
            await new Promise(resolve => setTimeout(resolve, typingDelay));

            const now = new Date();

            // Add single message
            setMessages(prev => [...prev, {
                id: prev.length + 1,
                sender: 'other',
                text: messages[index].text,
                image: messages[index]?.image_url,
                voice: messages[index]?.voice_url,
                time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                timestamp: now.getTime()
            }]);
            // Play notification sound and increment unread count if tab is not active
            if (!isTabActiveRef.current) {
                try {
                    notificationSound.play();
                    // Increment unread count
                    setUnreadCount(prev => prev + 1);
                } catch (error) {
                    console.error('Error playing notification sound:', error);
                }
            }

            setIsTyping(false);

            // If there are more messages, wait a bit and continue
            if (index + 1 < messages.length) {
                await new Promise(resolve => setTimeout(resolve, 200)); // Small pause between messages
                await addMessageWithDelay(messages, index + 1);
            }
        }
    };

    const sendPendingMessages = async (messagesToSend) => {
        try {
            // Mark all messages as sending (this will trigger re-render and show white ticks)
            const messageIds = messagesToSend.map(msg => msg.id);
            setSendingMessageIds(prev => new Set([...prev, ...messageIds]));

            setIsTyping(true);

            const messages = messagesToSend.map(msg => ({
                text: msg.text,
                image_url: null,
                type: 'text'
            }));
            if (selectedImage) {
                messages[messages.length - 1].image_url = selectedImage?.serverUrl;
            }
            let data = null;
            if (isUnidentifiedUser) {
                data = await apiCall.post('/chat/reply/v2', JSON.stringify({
                    messages: messages,
                    user_id: localStorage.getItem('user_id'),
                    character_id: selectedCharacter?._id
                }));
            }
            else {
                data = await apiCall.post('/chat/reply', JSON.stringify({
                    messages: messages,
                    character_id: selectedCharacter?._id
                }));
            }

            // Mark messages as sent (remove from sending state)
            setSendingMessageIds(prev => {
                const newSet = new Set(prev);
                messageIds.forEach(id => newSet.delete(id));
                return newSet;
            });

            if (data.status && data.data) {
                // Instead of adding all messages at once, add them sequentially
                await addMessageWithDelay(data.data);
            } else if (data.status === false && data.error_code === 1020) {
                // Free credits expired - show subscription modal
                // setShowSubscriptionModal(true);
                
                // Remove user messages from UI
                setMessages(prev => prev.filter(msg => !messagesToSend.some(sentMsg => sentMsg.id === msg.id)));
                
                // Remove from sending state
                const messageIds = messagesToSend.map(msg => msg.id);
                setSendingMessageIds(prev => {
                    const newSet = new Set(prev);
                    messageIds.forEach(id => newSet.delete(id));
                    return newSet;
                });
                
                setIsTyping(false);
            }

        } catch (error) {
            console.error('Failed to send messages:', error);

            // Remove from sending state on error
            const messageIds = messagesToSend.map(msg => msg.id);
            setSendingMessageIds(prev => {
                const newSet = new Set(prev);
                messageIds.forEach(id => newSet.delete(id));
                return newSet;
            });

            setIsTyping(false);
        }
    };

    const handleImageSelect = async (file) => {
        if (file) {
            setIsLoading(true);
            try {
                // Create local blob URL for immediate display
                const imageUrl = URL.createObjectURL(file);
                
                // Request signed URL for upload
                let response = null;
                if (isUnidentifiedUser) {
                    const queryParams = new URLSearchParams({
                        user_id: localStorage.getItem('user_id')
                    })
                    response = await apiCall.get(`/image/request/signed/url/v2` + '?' + queryParams.toString());
                }
                else {
                    response = await apiCall.get('/image/request/signed/url');
                }
                if (!response.status) {
                    throw new Error('Failed to get signed URL');
                }

                const signedUrl = response.data;

                // Upload to GCS immediately
                const uploadResponse = await fetch(signedUrl, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': file.type
                    }
                });

                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload image');
                }

                // Store the file blob and local URL for rendering, and the server URL for backend
                setSelectedImage({
                    file,
                    preview: imageUrl,
                    localUrl: imageUrl, // For frontend rendering
                    serverUrl: signedUrl.split('?')[0] // For backend communication
                });
            } catch (error) {
                console.error('Error uploading image:', error);
            }
            setIsLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        if (e) {
            e.preventDefault();
        }
        setInputMessage('');
        // Remove setInputPlaceholder here
        // setInputPlaceholder(`Talk to ${profileData?.name || 'Dia'}`);
        setSelectedImage(null);

        // Reset textarea height
        const textarea = document.querySelector('.message-input');
        if (textarea) {
            textarea.style.height = '16px';
        }
        if (inputMessage.trim() || selectedImage) {
            // Track chat message sent event
            trackEvent('chat_message_sent', {
                has_image: !!selectedImage,
                message_length: inputMessage.trim().length
            });
            try {
                // Add message to UI immediately
                const newMessage = {
                    id: messages.length + pendingMessages.length + 1,
                    sender: 'me',
                    text: inputMessage,
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                // If there's an image, add it to the message
                if (selectedImage) {
                    newMessage.type = 'image';
                    newMessage.image = selectedImage.localUrl; // Use local URL for rendering
                    newMessage.serverImageUrl = selectedImage.serverUrl; // Store server URL for backend
                }

                // Add to UI messages
                setMessages(prev => [...prev, newMessage]);
                const newCount = userMessageCount + 1;
                setUserMessageCount(newCount);
                if (isUnidentifiedUser && newCount > FREE_ALLOWED_MESSAGES) {
                    setShowInfoModal(true);
                }

                if (selectedImage) {
                    // For image messages, send immediately and track sending status
                    setSendingMessageIds(prev => new Set([...prev, newMessage.id]));
                    await sendPendingMessages([newMessage]);
                    setSendingMessageIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(newMessage.id);
                        return newSet;
                    });
                    setSelectedImage(null); // Clear the selected image after sending
                } else {
                    // For text messages, use the existing delay logic
                    const updatedPendingMessages = [...pendingMessages, newMessage];
                    setPendingMessages(updatedPendingMessages);

                    // Reset the typing timeout
                    if (typingTimeout) {
                        clearTimeout(typingTimeout);
                    }

                    // Start new timeout for sending messages
                    const newTimeout = setTimeout(() => {
                        sendPendingMessages(updatedPendingMessages);
                        setPendingMessages([]); // Clear pending messages after sending
                    }, 3000);

                    setTypingTimeout(newTimeout);
                }

                // Clear input
                setInputMessage('');
                setSelectedImage(null); // Clear selected image after sending any message
            } catch (error) {
                console.error('Error sending message:', error);
            }
        }

        // Keep keyboard open by focusing the input
        const inputElement = document.querySelector('.message-input');
        if (inputElement) {
            inputElement.focus();
        }
    };

    const handleInputChange = (e) => {
        const maxLength = 1000; // Set your desired max length here
        const value = e.target.value;
        
        // Truncate value if it exceeds max length (allows pasting but limits length)
        const truncatedValue = value.length > maxLength ? value.substring(0, maxLength) : value;
        
        setInputMessage(truncatedValue);

        // Auto-resize textarea
        const textarea = e.target;
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = Math.max(newHeight, 16) + 'px';

        // Clear existing timeout
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Only set new timeout if there are pending messages or current input
        if (pendingMessages.length > 0 || truncatedValue.trim()) {
            // Set new timeout
            const newTimeout = setTimeout(() => {
                if (pendingMessages.length > 0) {
                    sendPendingMessages(pendingMessages);
                    setPendingMessages([]); // Clear pending messages after sending
                }
            }, 2000);

            setTypingTimeout(newTimeout);
        }
    };

    const startRecording = async () => {
        try {
            // Clear any existing interval FIRST
            if (recordingInterval) {
                clearInterval(recordingInterval);
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = []

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data)
                }
            };

            recorder.onstop = async () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                setRecordedBlob(blob);
                stream.getTracks().forEach(track => track.stop());
                let recordingDuration = recordingTimeRef.current;

                // Check the ref value at the time onstop executes
                if (!isCancelledRef.current) {
                    await sendVoiceMessage(blob, recordingDuration);
                }

                setIsRecording(false);
                setRecordedBlob(null);
                setRecordingTime(0);
                recordingTimeRef.current = 0; // Reset ref too
                clearInterval(recordingInterval);
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
            setRecordingTime(0);
            setRecordingInterval(null);


            const interval = setInterval(() => {
                setRecordingTime(prev => {
                    const newTime = prev + 1;
                    recordingTimeRef.current = newTime; // Keep ref in sync
                    return newTime;
                });
            }, 1000);
            setRecordingInterval(interval);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Could not access microphone. Please check permissions.');
        }
    };

    const stopRecording = async () => {
        isCancelledRef.current = false;
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop(); // This will trigger the onstop handler
        }
    };

    const cancelRecording = () => {
        isCancelledRef.current = true;
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleVoiceUpload = async (blob) => {
        if (!blob) return null;

        setIsLoading(true);
        try {
            // Convert blob to file
            const file = new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });

            // Create local blob URL for immediate playback
            const localVoiceUrl = URL.createObjectURL(blob);

            // Request signed URL for voice message
            let response = null;
            if (isUnidentifiedUser) {
                const queryParams = new URLSearchParams({
                    user_id: localStorage.getItem('user_id')
                });
                response = await apiCall.get(`/voice/request/signed/url/v2?${queryParams.toString()}`);
            } else {
                response = await apiCall.get('/voice/request/signed/url');
            }

            if (!response.status) {
                throw new Error('Failed to get signed URL for voice message');
            }

            const signedUrl = response.data;

            // Upload to server
            const uploadResponse = await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload voice message');
            }

            return {
                localUrl: localVoiceUrl, // For frontend playback
                serverUrl: signedUrl.split('?')[0] // For backend communication
            };
        } catch (error) {
            console.error('Error uploading voice message:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Handle voice message send
    const sendVoiceMessage = async (blob, recordingDuration) => {
        if (!blob) return;
        const voiceUrls = await handleVoiceUpload(blob);
        if (!voiceUrls) return;
        
        const newMessage = {
            id: messages.length + pendingMessages.length + 1,
            sender: 'me',
            text: '',
            voice: voiceUrls.localUrl, // Use local URL for playback
            serverVoiceUrl: voiceUrls.serverUrl, // Store server URL for backend
            voiceDuration: recordingDuration,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: new Date().getTime()
        };

        setMessages(prev => [...prev, newMessage]);
        setSendingMessageIds(prev => new Set([...prev, newMessage.id]));

        const messages_to_send = [{
            text: '',
            voice_url: voiceUrls.serverUrl, // Send server URL to backend
            voice_duration: recordingDuration,
            type: 'voice'
        }];

        setIsTyping(true);
        setIsRecording(false);
        setRecordedBlob(null);
        setRecordingTime(0);
        recordingTimeRef.current = 0; // Reset ref too
        clearInterval(recordingInterval);

        try {
            let data = null;
            if (isUnidentifiedUser) {
                data = await apiCall.post('/chat/reply/v2', JSON.stringify({
                    messages: messages_to_send,
                    user_id: localStorage.getItem('user_id'),
                    character_id: selectedCharacter?._id
                }));
            } else {
                data = await apiCall.post('/chat/reply', JSON.stringify({
                    messages: messages_to_send,
                    character_id: selectedCharacter?._id
                }));
            }
            setSendingMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(newMessage.id);
                return newSet;
            });

            if (data.status && data.data) {
                await addMessageWithDelay(data.data);
            } else if (data.status === false && data.error_code === 1020) {
                // Free credits expired - show subscription modal
                // setShowSubscriptionModal(true);
                
                // Remove voice message from UI
                setMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
                
                // Remove from sending state
                setSendingMessageIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(newMessage.id);
                    return newSet;
                });
            }
        } catch (error) {
            console.error('Failed to send voice message:', error);
            setSendingMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(newMessage.id);
                return newSet;
            });
        }

        setIsTyping(false);
    };

    async function createAudioWithFullDownload(voiceUrl) {
        // Safari has issues with blob URLs, so we need to handle them differently
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        if (voiceUrl.startsWith('blob:') && isSafari) {
            // For Safari, convert blob URL to a format it can handle
            try {
                const response = await fetch(voiceUrl);
                const blob = await response.blob();
                
                // Create a new blob with proper MIME type for Safari
                const audioBlob = new Blob([blob], { type: 'audio/webm' });
                const safariCompatibleUrl = URL.createObjectURL(audioBlob);
                
                const audio = new Audio(safariCompatibleUrl);
                
                // Store the URL for cleanup
                audio._safariBlobUrl = safariCompatibleUrl;
                
                return audio;
            } catch (error) {
                console.error('Safari blob conversion failed:', error);
                // Fallback to original approach
                const audio = new Audio(voiceUrl);
                return audio;
            }
        } else {
            // For non-Safari browsers, use the original approach
            const audio = new Audio(voiceUrl);
            return audio;
        }
    }

    const handleVoiceError = async (message) => {
        // If the voice fails to load and we have a server URL, try to fetch it
        if (message.serverVoiceUrl) {
            try {
                const response = await fetch(message.serverVoiceUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    const localUrl = URL.createObjectURL(blob);
                    
                    // Clean up old blob URL if it exists
                    if (message.voice && message.voice.startsWith('blob:')) {
                        URL.revokeObjectURL(message.voice);
                    }
                    
                    // Update the message with the local URL
                    setMessages(prev => prev.map(msg => 
                        msg.id === message.id 
                            ? { ...msg, voice: localUrl }
                            : msg
                    ));
                    
                    // Update the audio element if it exists
                    const existingAudio = audioElements.get(message.id);
                    if (existingAudio) {
                        existingAudio.src = localUrl;
                    }
                }
            } catch (error) {
                console.error('Failed to load voice message:', error);
            }
        }
    };

    // Function to convert server URLs to local blob URLs (useful when bucket becomes private)
    const convertServerUrlsToLocal = async () => {
        const updatedMessages = [];
        
        for (const message of messages) {
            let updatedMessage = { ...message };
            
            // Convert image URLs
            if (message.serverImageUrl && message.image === message.serverImageUrl) {
                try {
                    const response = await fetch(message.serverImageUrl);
                    if (response.ok) {
                        const blob = await response.blob();
                        const localUrl = URL.createObjectURL(blob);
                        updatedMessage.image = localUrl;
                    }
                } catch (error) {
                    console.error('Failed to convert image URL:', error);
                }
            }
            
            // Convert voice URLs
            if (message.serverVoiceUrl && message.voice === message.serverVoiceUrl) {
                try {
                    const response = await fetch(message.serverVoiceUrl);
                    if (response.ok) {
                        const blob = await response.blob();
                        const localUrl = URL.createObjectURL(blob);
                        updatedMessage.voice = localUrl;
                    }
                } catch (error) {
                    console.error('Failed to convert voice URL:', error);
                }
            }
            
            updatedMessages.push(updatedMessage);
        }
        
        setMessages(updatedMessages);
    };

    // Play/pause voice message
    const toggleVoicePlayback = async (messageId, voiceUrl) => {
        const audioElement = audioElements.get(messageId);

        if (audioElement) {
            if (playingAudios.has(messageId)) {
                audioElement.pause();
                setPlayingAudios(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(messageId);
                    return newSet;
                });
            } else {
                try {
                    await audioElement.play();
                    setPlayingAudios(prev => new Set([...prev, messageId]));
                } catch (error) {
                    console.error('Audio play failed:', error);
                    // Safari might block audio without user interaction
                    if (error.name === 'NotAllowedError') {
                        console.log('Audio blocked by browser - user interaction required');
                    }
                }
            }
        } else {
            // Create new audio element
            const audio = await createAudioWithFullDownload(voiceUrl);
            
            // Add error handler for voice loading
            audio.onerror = async () => {
                const message = messages.find(msg => msg.id === messageId);
                if (message) {
                    await handleVoiceError(message);
                }
            };
            
            audio.onended = () => {
                setPlayingAudios(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(messageId);
                    return newSet;
                });
            };

            setAudioElements(prev => new Map([...prev, [messageId, audio]]));
            
            try {
                await audio.play();
                setPlayingAudios(prev => new Set([...prev, messageId]));
            } catch (error) {
                console.error('Audio play failed:', error);
                if (error.name === 'NotAllowedError') {
                    console.log('Audio blocked by browser - user interaction required');
                } else if (error.name === 'NotSupportedError') {
                    // Safari specific blob URL issue - try alternative approach
                    console.log('Safari blob URL issue detected, trying alternative approach');
                    await handleSafariBlobIssue(messageId, voiceUrl);
                }
            }
        }
    };

    // Handle Safari-specific blob URL issues
    const handleSafariBlobIssue = async (messageId, voiceUrl) => {
        try {
            // For Safari, try to convert the blob to a different format
            const response = await fetch(voiceUrl);
            const blob = await response.blob();
            
            // Try different MIME types that Safari supports better
            const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav'];
            
            for (const mimeType of mimeTypes) {
                try {
                    const newBlob = new Blob([blob], { type: mimeType });
                    const newUrl = URL.createObjectURL(newBlob);
                    
                    const audio = new Audio(newUrl);
                    audio._safariBlobUrl = newUrl;
                    
                    // Test if this audio can be loaded
                    await new Promise((resolve, reject) => {
                        audio.oncanplaythrough = resolve;
                        audio.onerror = reject;
                        audio.load();
                    });
                    
                    // If we get here, the audio loaded successfully
                    audio.onended = () => {
                        setPlayingAudios(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(messageId);
                            return newSet;
                        });
                    };
                    
                    setAudioElements(prev => new Map([...prev, [messageId, audio]]));
                    
                    try {
                        await audio.play();
                        setPlayingAudios(prev => new Set([...prev, messageId]));
                        console.log('Safari blob issue resolved with MIME type:', mimeType);
                        return;
                    } catch (playError) {
                        console.log('Play failed for MIME type:', mimeType, playError);
                        URL.revokeObjectURL(newUrl);
                    }
                } catch (mimeError) {
                    console.log('MIME type failed:', mimeType, mimeError);
                }
            }
            
            console.error('All Safari blob conversion attempts failed');
        } catch (error) {
            console.error('Safari blob issue handling failed:', error);
        }
    };

    const onLogout = async () => {
        window.AndroidBridge.triggerLogout();
    }

    // Cleanup timeout on component unmount
    useEffect(() => {
        return () => {
            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }
        };
    }, [typingTimeout]);

    // Fetch user profile on component load
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const userResponse = await apiCall.get('/users/get/user/details');
                const apiUserId = userResponse.data?._id || userResponse._id;
                setUserId(apiUserId);
            } catch (error) {
                console.log('Failed to fetch user profile:', error);
            }
        };

        fetchUserProfile();
    }, [isUnidentifiedUser]);

    // Update subscription status when prop changes
    useEffect(() => {
        if (hasSubscriptionProp !== undefined) {
            setHasSubscription(hasSubscriptionProp);
        } else if (isUnidentifiedUser) {
            // Unidentified users don't have subscriptions
            setHasSubscription(false);
        }
    }, [hasSubscriptionProp, isUnidentifiedUser]);

    // Call Android bridge function when chat page opens for the first time
    useEffect(() => {
        try {
            window.AndroidBridge.chatPageOpened();
        } catch (error) {
            console.log('Android bridge chatPageOpened not available:', error);
        }
    }, []);

    // Function to check if update modal should be shown based on app version
    const shouldShowUpdateModalToday = () => {
        // Check app version - only show for versions < 3.0.5
        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        const appVersion = params.get('app_version');
        
        if (!appVersion) {
            // If no app version, assume it's an old version and show modal
            return true;
        }
        
        // Parse version and check if it's less than 3.0.5
        const versionParts = appVersion.split('.').map(part => parseInt(part, 10));
        const major = versionParts[0] || 0;
        const minor = versionParts[1] || 0;
        const patch = versionParts[2] || 0;
        
        // Compare with 3.0.5
        if (major < 3) return true;
        if (major === 3 && minor < 0) return true;
        if (major === 3 && minor === 0 && patch < 5) return true;
        
        return false;
    };

    // Check if update modal should be shown based on app version
    useEffect(() => {
        const checkAndShowUpdateModal = () => {
            if (shouldShowUpdateModalToday()) {
                setShowUpdateModal(true);
            }
        };

        checkAndShowUpdateModal();
    }, []);


    const openModal = (title, message, action) => {
        setModalConfig({ title, message, action });
        setShowModal(true);
    };


    const handleReportContent = () => {
        trackEvent('report_content_clicked');
        setShowNavMenu(false);
        setShowReportModal(true);
    };

    const handleSubmitReport = async () => {
        if (!reportDescription.trim()) {
            return;
        }
        
        try {
            trackEvent('report_submitted');
            // Here you would typically send the report to your backend
            // await apiCall.post('/reports', { description: reportDescription });
            console.log('Report submitted:', reportDescription);
            
            // Reset form and close modal
            setReportDescription('');
            setShowReportModal(false);
            
        } catch (error) {
            console.error('Failed to submit report:', error);
            alert('Failed to submit report. Please try again.');
        }
    };

    const handleCancelReport = () => {
        setReportDescription('');
        setShowReportModal(false);
    };

    // Add typing indicator component
    const TypingIndicator = () => (
        <div className="message-container other" style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', alignItems: 'center' }}>
            <div className="typing-indicator">
                <div className="typing-indicator-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <div className="typing-indicator-text">
                    {selectedCharacter?.name || 'Dia'} is typing
                </div>
            </div>
        </div>
    );

    const loginUser = async () => {
        setShowLoginModal(true);
        setShowInfoModal(false);
        trackEvent('login_clicked');
    };

    const closeLoginModal = async () => {
        setShowLoginModal(false);
        setShowInfoModal(true);
    };


    const displayTitle = variant === 'deva' ? (displayName || 'Deva') : (selectedCharacter?.name || profileData?.name);
    const headerAvatar =
        (variant === 'deva' ? displayAvatarUrl : null) ||
        (variant === 'deva' ? (selectedCharacter?.image_url || DiaProfilePicture) : (selectedCharacter?.image_url || profileData?.image_url || DiaProfilePicture));

    const showDevaStarter = variant === 'deva' && messages.length === 0 && !isLoading;

    return (
        <div className={`chat-container ${variant === 'deva' ? 'deva-chat' : ''}`} style={{ position: 'relative' }}>
            {showProfile && (
                <div
                    className="profile-overlay"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        height: '100dvh',
                        zIndex: 1000,
                        overflow: 'hidden'
                    }}
                >
                    <div>
                        <Profile
                            profileData={selectedCharacter ? selectedCharacter : profileData}
                            setShowProfile={setShowProfile}
                            setProfileData={setProfileData}
                            readOnly={true}
                        />
                    </div>
                </div>
            )}
            <div className={`chat-header-container ${variant === 'deva' ? 'deva-chat-header-container' : ''}`}>
                    <div className="chat-header" style={{
                    justifyContent: 'space-between',
                    maxWidth: '768px',
                    margin: '0 auto',
                    position: 'relative'
                }}>
                    {variant === 'deva' ? (
                        <div className="contact-info deva-contact-info">
                            <div className="contact-avatar deva-avatar">
                                <img
                                    src={headerAvatar}
                                    alt={displayTitle}
                                    style={{
                                        height: '40px',
                                        width: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                            <div className="contact-details">
                                <div className="contact-name deva-contact-name">{displayTitle}</div>
                                <div className="deva-status-row">
                                    <span className="deva-online-dot" aria-hidden="true" />
                                    <span className="deva-status-text">Online</span>
                                </div>
                            </div>
                        </div>
                    ) : showProfile ? (
                        <div onClick={() => setShowProfile(false)}>
                            <img src={backIcon} style={{ height: '20px', width: '20px', cursor: 'pointer', filter: 'brightness(0) saturate(100%) invert(20%) sepia(15%) saturate(1200%) hue-rotate(175deg) brightness(95%) contrast(90%)' }} />
                        </div>
                    ) : selectedCharacter ? (
                        // Show back button with character info when a character is selected
                        <div className="contact-info">
                            <img 
                                src={backIcon} 
                                onClick={onBackToCharacterSelection}
                                style={{ 
                                    height: '20px', 
                                    width: '20px', 
                                    cursor: 'pointer', 
                                    filter: 'brightness(0) saturate(100%) invert(20%) sepia(15%) saturate(1200%) hue-rotate(175deg) brightness(95%) contrast(90%)',
                                    marginRight: '12px'
                                }} 
                            />
                            <div className="contact-avatar" onClick={() => setProfileImageInModal(selectedCharacter.image_url)}>
                                <img
                                    src={selectedCharacter.image_url}
                                    alt={selectedCharacter.name}
                                    style={{
                                        cursor: 'pointer',
                                        height: '40px',
                                        width: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                            <div className="contact-details">
                                <div className='contact-name' onClick={() => setShowProfile(true)} style={{ cursor: 'pointer' }}>
                                    {selectedCharacter.name}
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Show profile info when no character is selected
                        <div className="contact-info" onClick={() => setShowProfile(true)}>
                            <div className="contact-avatar">
                                <img
                                    src={profileData?.image_url || DiaProfilePicture}
                                    alt="Dia"
                                    onClick={() => { trackEvent('profile_avatar_clicked'); setShowProfile(true); }}
                                    style={{
                                        cursor: 'pointer',
                                        height: '40px',
                                        width: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                            <div className="contact-details">
                                <div
                                    className='contact-name'
                                    onClick={() => setShowProfile(true)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {profileData?.name}
                                </div>
                            </div>
                        </div>
                    )}
                    {variant === 'deva' ? null : <div className="header-actions" style={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
                    {(selectedCharacter?.social_media_handle?.instagram) && (
                            <img
                                src={instagramIcon}
                                style={{ marginRight: "10px", height: "22px", cursor: 'pointer' }}
                                onClick={handleInstagramClick}
                            />
                        )}
                        
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                                src={shareIcon}
                                className="chat-action-icon"
                                style={{ marginRight: "10px", height: "18px", cursor: 'pointer' }}
                                onClick={handleShare}
                            />
                            {showCopied && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    backgroundColor: '#333',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    animation: 'fadeInOut 2s ease-in-out'
                                }}>
                                    Link copied!
                                </div>
                            )}
                        </div>
                       
                        {!isUnidentifiedUser ? <img
                            src={showNavMenu ? closeIcon : threeDotsIcon}
                            className={showNavMenu ? "close-icon" : "three-dots-icon"}
                            style={{
                                transform: showNavMenu ? "rotate(180deg)" : "rotate(0deg)"
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowNavMenu(!showNavMenu);
                            }}
                        /> : <button style={{ width: '60px', fontSize: '14px', padding: '10px' }} className="guest-modal-login-btn" onClick={() => setShowLoginModal(true)}>Log In</button>}
                    </div>}

                    <div className={`nav-menu-container ${showNavMenu ? 'show' : ''}`}
                        style={{
                            position: 'absolute',
                            top: '55px',
                            right: '0px',
                            width: 'auto',
                            minWidth: '200px',
                            zIndex: 10,
                            pointerEvents: showNavMenu ? 'auto' : 'none'
                        }}
                        >
                        <Navigation setShowProfile={setShowProfile} openModal={openModal} setIsLoading={setIsLoading} darkMode={darkMode} toggleDarkMode={toggleDarkMode} setShowNavMenu={setShowNavMenu} selectedCharacter={selectedCharacter} setMessages={setMessages} fetchChatHistory={fetchChatHistory} handleReportContent={handleReportContent}/>
                    </div>
                </div>
            </div>

            <div className="chat-content">
                <div
                    className="messages-area"
                    onScroll={handleScroll}
                >
                    {showDevaStarter && (
                        <div className="deva-starter">
                            <div className="message-container other">
                                <div className="message-bubble other deva-starter-bubble">
                                    <div>Good to see you.</div>
                                    <div style={{ marginTop: '10px' }}>Say what's on your mind.</div>
                                </div>
                            </div>
                            <div className="deva-starter-time">
                                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    )}
                    {renderMessagesWithDateSeparators()}
                    {isTyping && <TypingIndicator />}
                    <div ref={messagesEndRef} />

                    {
                        showScrollButton && (
                            <div style={{
                                position: 'absolute',
                                bottom: '95px',
                                right: '10px',
                                zIndex: 100
                            }}>
                                <img
                                    src={darkMode ? scrollIconDark : scrollIcon}
                                    onClick={() => scrollToBottom()}
                                    style={{
                                        height: '40px',
                                        cursor: 'pointer'
                                    }}
                                />
                            </div>
                        )
                    }
                </div>
                <div style={{ backgroundColor: '#F0F7F0' }}>
                <div style={{
                    position: 'sticky',
                    bottom: 0,
                    padding: '0px 12px',
                    zIndex: 100,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderTop: '1px solid rgba(73, 148, 62, 0.1)'
                }} className='input-area'>
                    {selectedImage && (
                        <div className="selected-image-preview">
                            <img src={selectedImage.preview} alt="Selected" />
                            <button onClick={() => {
                                // Clean up blob URL
                                if (selectedImage.localUrl) {
                                    URL.revokeObjectURL(selectedImage.localUrl);
                                }
                                setSelectedImage(null);
                            }}>×</button>
                        </div>
                    )}
                    <div className='text-input-area'>
                        <div>
                            {/* Show photo and mic icons only when NOT recording and no recorded blob */}
                            {!isRecording && (
                                <>
                            <img
                                src={choosePhotoIcon}
                                onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                                    input.onchange = (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            handleImageSelect(file);
                                        }
                                    };
                                    input.click();
                                }}
                                className="image-upload-icon"
                                alt="Upload image"
                            />
                                </>
                            )}
                        </div>

                        {/* Recording indicator */}
                        {isRecording && (
                            <div className="recording-indicator">
                                <div>
                                    <img
                                        src={deleteIcon}
                                        alt="Delete Recoding"
                                        className="send-message-icon"
                                        onClick={cancelRecording}
                                        style={{ cursor: 'pointer', height: '30px', width: '30px', marginRight: '8px', filter: darkMode ? 'brightness(0) invert(1)' : 'none' }}
                                    />
                                </div>
                                <div className="recording-waveform">
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                    <div className="waveform-bar"></div>
                                </div>
                                <span style={{ fontSize: '14px', color: '#666', marginRight: '8px' }}>
                                    {formatTime(recordingTime)}
                                </span>
                                <div><img
                                    src={sendMessageIcon}
                                    onClick={stopRecording}
                                    style={{ cursor: 'pointer', height: '40px', width: '40px' }}
                                    className='send-message-icon'
                                    alt="Send recording"
                                />
                                </div>
                            </div>
                        )}


                        {!isRecording && (
                                <textarea
                                    placeholder={inputPlaceholder}
                                    className="message-input"
                                    value={inputMessage}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    onInput={(e) => {
                                        // Auto-resize textarea
                                        e.target.style.height = 'auto';
                                        const newHeight = Math.min(e.target.scrollHeight, 120);
                                        e.target.style.height = Math.max(newHeight, 16) + 'px';
                                    }}
                                    rows="1"
                                    style={{
                                        outline: 'none',
                                        resize: 'none',
                                        minHeight: '20px',
                                        maxHeight: '120px',
                                        overflowY: 'auto',
                                        height: '16px'
                                    }}
                                />
                        )}

                        {/* Send icon - show when there's text OR selected image OR recorded blob */}

                        {!isRecording ? (
                            inputMessage.length > 0 || selectedImage ? (
                                <img
                                    src={darkMode?sendMessageIconDark:sendMessageIcon}
                                    alt="Send message"
                                    className="send-message-icon"
                                    onClick={handleSendMessage}
                                    style={{
                                        opacity: (inputMessage.trim() || selectedImage || recordedBlob) ? 1 : 0.5,
                                        cursor: (inputMessage.trim() || selectedImage || recordedBlob) ? 'pointer' : 'not-allowed',
                                        display: isRecording ? 'none' : 'block' // Optional, since you're already gating on isRecording
                                    }}
                                />
                            ) : (
                                <img
                                    src={darkMode ? micIconDark : micIcon}
                                    onClick={startRecording}
                                    style={{ cursor: 'pointer', height: '40px', width: '40px' }}
                                    alt="Start recording"
                                    className="send-message-icon"
                                />
                            )
                        ) : null}

                    </div>
                </div>
                    {variant !== 'deva' && (
                        <div className="privacy-notice">
                            This is A.I. bot and not real person
                        </div>
                    )}
                </div>
            </div>

            {
                showModal && (
                    <div
                        className="modal-overlay"
                        onClick={() => setShowModal(false)}
                    >
                        <div
                            className="confirm-modal-content"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="confirm-modal-question">{modalConfig.message}</div>
                            <div className="confirm-modal-buttons">
                                <button className="confirm-modal-yes" onClick={() => {
                                    setShowModal(false);
                                    modalConfig.action();
                                }}>Yes</button>
                                <button className="confirm-modal-no" onClick={() => setShowModal(false)}>No</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {showInfoModal && (
                <div className="modal-overlay">
                    <div className="guest-modal-card">
                        <div className="guest-modal-title">Hey There!</div>
                        <div className="guest-modal-heading">You're in Guest Mode.</div>
                        {userMessageCount <= FREE_ALLOWED_MESSAGES && <div className="guest-modal-subtitle">Your chats won't be saved unless you login.</div>}
                        <div className="guest-modal-unlock">Login to Unlock Dia’s:</div>
                        <div className="guest-modal-features-row">
                            <div className="guest-modal-feature">
                                <img src={memoryFeatureLogo} className='feature-image' alt="Memory" />
                            </div>
                            <div className="guest-modal-feature">
                                <img src={smartRepliesFeatureLogo} className='feature-image' alt="Smart Replies" />
                            </div>
                            <div className="guest-modal-feature">
                                <img src={personalisationFeatureLogo} className='feature-image' alt="Personalizations" />
                            </div>
                        </div>
                        <div className="guest-modal-buttons">
                            <button className="guest-modal-login-btn" onClick={loginUser}>Log In</button>
                            {userMessageCount <= FREE_ALLOWED_MESSAGES && <button className="guest-modal-guest-btn" onClick={() => { trackEvent('continue_as_stranger_clicked'); setShowInfoModal(false); }}>Continue as Guest</button>}
                        </div>
                    </div>
                </div>
            )}

            {
                imageInModal && (
                    <div className="image-modal-overlay" onClick={() => setImageInModal(null)}>
                        <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    className="close-image-button"
                                    onClick={() => setImageInModal(null)}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 1
                                    }}
                                >×</button>
                                <img src={imageInModal} alt="Full size" className="modal-image" />
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Profile Image Modal */}
            {
                profileImageInModal && (
                    <div className="image-modal-overlay" onClick={() => setProfileImageInModal(null)}>
                        <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    className="close-image-button"
                                    onClick={() => setProfileImageInModal(null)}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 1
                                    }}
                                >×</button>
                                <img src={profileImageInModal} alt="Profile" className="modal-image" />
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showLoginModal && (
                    <div className="modal-overlay">
                        <Login onClose={closeLoginModal} />
                    </div>
                )
            }

            {/* Report Content Modal */}
            {showReportModal && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowReportModal(false)}
                >
                    <div
                        className="report-modal-content"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="report-modal-title">Report Content</div>
                        <textarea
                            className="report-modal-textarea"
                            placeholder="Describe the issue here..."
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            rows={4}
                        />
                        <div className="report-modal-buttons">
                            <button 
                                className="report-modal-cancel" 
                                onClick={handleCancelReport}
                            >
                                Cancel
                            </button>
                            <button 
                                className="report-modal-submit" 
                                onClick={handleSubmitReport}
                                disabled={!reportDescription.trim()}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Modal
            {showUpdateModal && (
                <div className="modal-overlay">
                    <div
                        className="update-modal-content"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="update-modal-title">Update Available</div>
                        <div className="update-modal-message">
                        Naye doston se milne ke liye, app update karo aaj hi!
                        </div>
                        <div className="update-modal-buttons">
                            <button 
                                className="update-modal-update" 
                                onClick={handleUpdateClick}
                                style={{ width: '100%' }}
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Subscription Modal */}
            {/* <SubscriptionModal
                isOpen={showSubscriptionModal}
                onClose={handleCloseSubscriptionModal}
                onPurchase={handleSubscriptionPurchase}
            /> */}

        </div>
    );
};

export default Chat;